import InvalidArgumentError from "../../Errors/InvalidArgumentError";

export default class SiSU {
    /**
     * Constructor
     */
    constructor(glucose) {
        this._validateValue(glucose);
        this._glucose = glucose;
    }

    /**
     * Returns the given Glucose value
     * @returns {Number}
     */
    get() {
        return this._glucose;
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "NON_FASTING_GLUCOSE";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "SISU_NON_FASTING_GLUCOSE";
    }

    /**
     * Validates the given Glucose value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateValue(glucose) {
        if (!(typeof glucose === "number") || Number.isNaN(glucose) || glucose < 0) {
            throw new InvalidArgumentError("Glucose must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Returns if the given Glucose is in the 'low' range
     *
     * @returns {boolean}
     */
    isLow() {
        return this._glucose < 4.0;
    }

    /**
     * Returns if the given Glucose is in the 'normal' range
     *
     * @returns {boolean}
     */
    isNormal() {
        return this._glucose >= 4.0 && this._glucose < 7.8;
    }

    /**
     * Returns if the given Glucose is in the 'elevated' range
     *
     * @returns {boolean}
     */
    isElevated() {
        return this._glucose >= 7.8 && this._glucose < 11.1;
    }

    /**
     * Returns if the given Glucose is in the 'high' range
     *
     * @returns {boolean}
     */
    isHigh() {
        return this._glucose >= 11.1;
    }

    /**
     * Returns an object representing various data for each Glucose range
     *
     * @returns {object}
     */
    static get ranges() {
        return {
            low: {
                key: "low",
                label: "Low",
                range: "< 4.00 mmol/L"
            },

            normal: {
                key: "normal",
                label: "Normal",
                range: "4.00 - 7.79 mmol/L"
            },

            elevated: {
                key: "elevated",
                label: "Elevated",
                range: "7.80 - 11.09 mmol/L"
            },

            high: {
                key: "high",
                label: "High",
                range: "11.10+ mmol/L"
            }
        };
    }

    /**
     * returns help information about the calculated range
     *
     * @returns {string}
     */
    get rangeInfo() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = SiSU.ranges.low.range;
                break;
            case this.isNormal():
                info = SiSU.ranges.normal.range;
                break;
            case this.isElevated():
                info = SiSU.ranges.elevated.range;
                break;
            case this.isHigh():
                info = SiSU.ranges.high.range;
                break;
        }

        return info;
    }

    /**
     * returns key about the calculated range
     *
     * @returns {string}
     */
    get key() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = SiSU.ranges.low.key;
                break;
            case this.isNormal():
                info = SiSU.ranges.normal.key;
                break;
            case this.isElevated():
                info = SiSU.ranges.elevated.key;
                break;
            case this.isHigh():
                info = SiSU.ranges.high.key;
                break;
        }

        return info;
    }

    /**
     * Returns label for the calculated range
     *
     * @returns {string}
     */
    get label() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = SiSU.ranges.low.label;
                break;
            case this.isNormal():
                info = SiSU.ranges.normal.label;
                break;
            case this.isElevated():
                info = SiSU.ranges.elevated.label;
                break;
            case this.isHigh():
                info = SiSU.ranges.high.label;
                break;
        }
        return info;
    }

    /**
     * Calculates a risk level number between 0 - 1 for each rating
     *
     * @return {number}
     */
    get riskLevel() {
        let totalRiskLevel = 4,
            riskLevel = 0;
        switch (true) {
            case this.isLow():
                riskLevel = 1 / (totalRiskLevel + 1);
                break;
            case this.isNormal():
                riskLevel = 2 / (totalRiskLevel + 1);
                break;
            case this.isElevated():
                riskLevel = 3 / (totalRiskLevel + 1);
                break;
            case this.isHigh():
                riskLevel = 4 / (totalRiskLevel + 1);
                break;
        }

        return riskLevel;
    }
}
